import { Component, OnDestroy, OnInit } from '@angular/core';
import { AppNotificationService } from '@core/services/app-notification.service';
import { BcmNavigationService } from '@modules/bcm/bcm-navigation.service';
import { BcmUserNavigationSettings, BcmUserSettingKey } from '@shared/models/bcm-settings-user';
import { BcmUserSettingsFacade } from '@bcmServices/settings/bcm-user-settings-facade';
import { FuseNavigationService } from '@fuse/components/navigation/navigation.service';
import { BcmNavigationItem } from '../../../../navigation/editable-navigation-bcm';
import { CdkDragDrop, moveItemInArray, transferArrayItem } from '@angular/cdk/drag-drop';
import { firstValueFrom, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { v4 as uuidv4 } from 'uuid';
import { ConfirmDialogService } from '@sharedComponents/dialogs/confirm-dialog/confirm-dialog.service';
import { ListItem, ListItemType } from '../../../../navigation/navigation.shared';
import { NavigationSlugs } from '../../../../navigation/navigation-slugs';
import { BcmTenantService } from '@modules/bcm/bcm-tenant.service';
import { UserService } from '@core/services/user.service';
import { TranslationService } from '@core/translation/translation.service';

@Component({
    selector: 'bcm-settings-navigation',
    templateUrl: './navigation.component.html',
    styleUrls: ['./navigation.component.scss'],
    standalone: false
})
export class NavigationComponent implements OnInit, OnDestroy {

    navigation: ListItem[];

    ListItemType = ListItemType;

    public itemTitlesEditable = false;

    public availableNavigationItems: BcmNavigationItem[] = [];

    public notAvailableNavigationItems: BcmNavigationItem[] = [];

    public onDragDrop$ = new Subject<CdkDragDrop<Array<ListItem>>>();

    private _unsubscribeAll = new Subject<void>();

    constructor(
        private _appNotificationService: AppNotificationService,
        private _confirmDialogService: ConfirmDialogService,
        private _bcmNavigationService: BcmNavigationService,
        private _fuseNavigationService: FuseNavigationService,
        private bcmTenantService: BcmTenantService,
        private userService: UserService,
        public settingsFacade: BcmUserSettingsFacade,
    ) {
    }

    ngOnInit(): void {
        this.navigation = this._bcmNavigationService.getNavigation(true);

        this.prepareDragDrop(this.navigation);

        this.prepareNotUsedItems();

        this.onDragDrop$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((event) => this.drop(event));
    }

    ngOnDestroy(): void {
        this._unsubscribeAll.next();
        this._unsubscribeAll.complete();
    }

    addNewNode(type: ListItemType): void {
        let title = '';

        switch (type) {
            case ListItemType.Group:
                title = TranslationService.translate('userSettings.navigation.newGroup');
                break;
            case ListItemType.Collapsable:
                title = TranslationService.translate('userSettings.navigation.newCollapsible');
                break;
        }

        this.navigation.push({
            type,
            children: [],
            id: 'c_' + uuidv4(),
            title,
        });

        this._appNotificationService.showSuccess(
            TranslationService.translate('userSettings.navigation.nodeAdded', {title})
        );
    }

    drop(event): void {
        if (event.previousContainer === event.container) {
            moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
        } else {
            transferArrayItem(
                event.previousContainer.data,
                event.container.data,
                event.previousIndex,
                event.currentIndex,
            );
        }
    }

    async save(): Promise<void> {

        const result = await firstValueFrom(
            this._confirmDialogService
                .setTheme('warn')
                .setTitle(TranslationService.translate('userSettings.navigation.confirmOverwriteTitle'))
                .setBody(TranslationService.translate('userSettings.navigation.confirmOverwriteBody'))
                .openAndReturnResult()
        );

        if (!result) {
            this._appNotificationService.showInfo(TranslationService.translate('userSettings.navigation.saveCancelled'));
            return;
        }

        const newSettings: BcmUserNavigationSettings = {
            state: this._bcmNavigationService.getCustomNavigation(this.navigation)
        };

        this._bcmNavigationService.setNavigation(this.navigation);

        this.settingsFacade
            .updateSingleSetting(BcmUserSettingKey.Navigation, newSettings)
            .add(() => {
                this._fuseNavigationService.unregister(NavigationSlugs.MainBcm);
                this._fuseNavigationService.register(
                    NavigationSlugs.MainBcm,
                    this._bcmNavigationService.getNavigation()
                );
                this._fuseNavigationService.setCurrentNavigation(NavigationSlugs.MainBcm);
            });
    }

    private getIdsRecursive(items: ListItem[]): string[] {
        let ids = [];
        items.forEach((childItem) => {
            ids.push(childItem.id);
            ids = ids.concat(this.getIdsRecursive(childItem.children));
        });
        return ids;
    }

    private prepareDragDrop(nodes: ListItem[]): void {
        nodes.forEach(node => {
            node.children = node.children || [];
            this.prepareDragDrop(node.children);
        });
    }

    private prepareNotUsedItems(): void {
        const usedIds = this.getIdsRecursive(this.navigation);

        const notUsedYet = this._bcmNavigationService.allNavigationItems
            .filter(item => item.notEditable !== true)
            .filter((item: ListItem) => !usedIds.includes(item.id));

        let tenantHasPermission: boolean;
        let userHasPermission: boolean;
        for (const item of notUsedYet) {
            tenantHasPermission = this._bcmNavigationService.hasTenantNavigationAccess(item);
            userHasPermission = this._bcmNavigationService.hasUserNavigationAccess(item);

            if (tenantHasPermission && userHasPermission) {
                this.availableNavigationItems.push(item);
            } else {

                if (!tenantHasPermission) {
                    item.subtitle = TranslationService.translate('userSettings.navigation.licenseSubtitle');
                    item.subtitleType = 'license';
                } else if (!userHasPermission) {
                    item.subtitle = TranslationService.translate('userSettings.navigation.permissionSubtitle');
                    item.subtitleType = 'permission';
                }

                this.notAvailableNavigationItems.push(item);
            }
        }

        this.notAvailableNavigationItems.sort((a, b) => {
            const label = (item: BcmNavigationItem) =>
                (item.translate ? TranslationService.translate(item.translate) : '') ||
                (item.title || '') ||
                item.id;
            return label(a).toLowerCase().localeCompare(label(b).toLowerCase());
        });
    }
}
